import { computed } from 'vue';
import { usePolicy } from 'dashboard/composables/usePolicy';

/**
 * Composable for checking granular permissions.
 * Uses the existing usePolicy().checkPermissions() which checks
 * the current user's permissions array (from AccountUser#permissions).
 *
 * Administrator always has all permissions.
 * Agents without custom_role have ['agent'] permissions.
 * Agents with custom_role have their role's permissions + ['custom_role'].
 */
export function usePermissions() {
  const { checkPermissions } = usePolicy();

  // ── Deals ──
  const canViewDeals = computed(() =>
    checkPermissions([
      'administrator',
      'agent',
      'deal_view_all',
      'deal_view_own',
    ])
  );
  const canCreateDeal = computed(() =>
    checkPermissions(['administrator', 'agent', 'deal_create'])
  );
  const canEditDeal = computed(() =>
    checkPermissions([
      'administrator',
      'agent',
      'deal_edit_all',
      'deal_edit_own',
    ])
  );
  const canDeleteDeal = computed(() =>
    checkPermissions(['administrator', 'deal_delete'])
  );
  const canExportDeals = computed(() =>
    checkPermissions(['administrator', 'deal_export'])
  );
  const canBulkOperateDeals = computed(() =>
    checkPermissions(['administrator', 'deal_bulk_operations'])
  );

  // ── Pipelines ──
  const canViewPipelines = computed(() =>
    checkPermissions(['administrator', 'agent', 'pipeline_view'])
  );
  const canCreatePipeline = computed(() =>
    checkPermissions(['administrator', 'pipeline_create'])
  );
  const canEditPipeline = computed(() =>
    checkPermissions(['administrator', 'pipeline_edit'])
  );
  const canDeletePipeline = computed(() =>
    checkPermissions(['administrator', 'pipeline_delete'])
  );

  // ── Forms ──
  const canViewForms = computed(() =>
    checkPermissions(['administrator', 'agent', 'form_view'])
  );
  const canCreateForm = computed(() =>
    checkPermissions(['administrator', 'form_create'])
  );
  const canEditForm = computed(() =>
    checkPermissions(['administrator', 'form_edit'])
  );
  const canDeleteForm = computed(() =>
    checkPermissions(['administrator', 'form_delete'])
  );
  const canPublishForm = computed(() =>
    checkPermissions(['administrator', 'form_publish'])
  );

  // ── Tasks ──
  const canViewTasks = computed(() =>
    checkPermissions([
      'administrator',
      'agent',
      'task_view_all',
      'task_view_own',
    ])
  );
  const canCreateTask = computed(() =>
    checkPermissions(['administrator', 'agent', 'task_create'])
  );
  const canEditTask = computed(() =>
    checkPermissions([
      'administrator',
      'agent',
      'task_edit_all',
      'task_edit_own',
    ])
  );
  const canDeleteTask = computed(() =>
    checkPermissions(['administrator', 'task_delete'])
  );

  // ── Tickets ──
  const canViewTickets = computed(() =>
    checkPermissions([
      'administrator',
      'agent',
      'ticket_view_all',
      'ticket_view_own',
    ])
  );
  const canCreateTicket = computed(() =>
    checkPermissions(['administrator', 'agent', 'ticket_create'])
  );
  const canEditTicket = computed(() =>
    checkPermissions([
      'administrator',
      'agent',
      'ticket_edit_all',
      'ticket_edit_own',
    ])
  );
  const canDeleteTicket = computed(() =>
    checkPermissions(['administrator', 'ticket_delete'])
  );

  // ── Segments ──
  const canViewSegments = computed(() =>
    checkPermissions(['administrator', 'agent', 'segment_view'])
  );
  const canCreateSegment = computed(() =>
    checkPermissions(['administrator', 'segment_create'])
  );
  const canEditSegment = computed(() =>
    checkPermissions(['administrator', 'segment_edit'])
  );
  const canDeleteSegment = computed(() =>
    checkPermissions(['administrator', 'segment_delete'])
  );

  // ── Products ──
  const canViewProducts = computed(() =>
    checkPermissions(['administrator', 'agent', 'product_view'])
  );
  const canCreateProduct = computed(() =>
    checkPermissions(['administrator', 'product_create'])
  );
  const canEditProduct = computed(() =>
    checkPermissions(['administrator', 'product_edit'])
  );
  const canDeleteProduct = computed(() =>
    checkPermissions(['administrator', 'product_delete'])
  );

  // ── Reports ──
  const canViewCrmReports = computed(() =>
    checkPermissions(['administrator', 'agent', 'report_crm_view'])
  );
  const canViewConversationReports = computed(() =>
    checkPermissions(['administrator', 'agent', 'report_conversation_view'])
  );
  const canViewAgentReports = computed(() =>
    checkPermissions(['administrator', 'report_agent_view'])
  );
  const canExportReports = computed(() =>
    checkPermissions(['administrator', 'report_export'])
  );

  // ── Contacts ──
  const canViewContacts = computed(() =>
    checkPermissions(['administrator', 'agent', 'contact_view'])
  );
  const canCreateContact = computed(() =>
    checkPermissions(['administrator', 'agent', 'contact_create'])
  );
  const canEditContact = computed(() =>
    checkPermissions(['administrator', 'agent', 'contact_edit'])
  );
  const canDeleteContact = computed(() =>
    checkPermissions(['administrator', 'contact_delete'])
  );
  const canExportContacts = computed(() =>
    checkPermissions(['administrator', 'contact_export'])
  );
  const canImportContacts = computed(() =>
    checkPermissions(['administrator', 'contact_import'])
  );

  // ── Companies ──
  const canViewCompanies = computed(() =>
    checkPermissions(['administrator', 'agent', 'company_view'])
  );
  const canCreateCompany = computed(() =>
    checkPermissions(['administrator', 'agent', 'company_create'])
  );
  const canEditCompany = computed(() =>
    checkPermissions(['administrator', 'agent', 'company_edit'])
  );
  const canDeleteCompany = computed(() =>
    checkPermissions(['administrator', 'company_delete'])
  );

  // ── Settings ──
  const canManageAgents = computed(() =>
    checkPermissions(['administrator', 'settings_manage_agents'])
  );
  const canManageRoles = computed(() =>
    checkPermissions(['administrator', 'settings_manage_roles'])
  );
  const canManageInboxes = computed(() =>
    checkPermissions(['administrator', 'settings_manage_inboxes'])
  );
  const canManageAccount = computed(() =>
    checkPermissions(['administrator', 'settings_manage_account'])
  );
  const canManageIntegrations = computed(() =>
    checkPermissions(['administrator', 'settings_manage_integrations'])
  );

  // ── Social ──
  const canViewSocial = computed(() =>
    checkPermissions(['administrator', 'social_view'])
  );
  const canManageSocial = computed(() =>
    checkPermissions(['administrator', 'social_manage'])
  );
  const canConfigureSocial = computed(() =>
    checkPermissions(['administrator', 'social_configure'])
  );

  return {
    // Deals
    canViewDeals,
    canCreateDeal,
    canEditDeal,
    canDeleteDeal,
    canExportDeals,
    canBulkOperateDeals,
    // Pipelines
    canViewPipelines,
    canCreatePipeline,
    canEditPipeline,
    canDeletePipeline,
    // Forms
    canViewForms,
    canCreateForm,
    canEditForm,
    canDeleteForm,
    canPublishForm,
    // Tasks
    canViewTasks,
    canCreateTask,
    canEditTask,
    canDeleteTask,
    // Tickets
    canViewTickets,
    canCreateTicket,
    canEditTicket,
    canDeleteTicket,
    // Segments
    canViewSegments,
    canCreateSegment,
    canEditSegment,
    canDeleteSegment,
    // Products
    canViewProducts,
    canCreateProduct,
    canEditProduct,
    canDeleteProduct,
    // Reports
    canViewCrmReports,
    canViewConversationReports,
    canViewAgentReports,
    canExportReports,
    // Contacts
    canViewContacts,
    canCreateContact,
    canEditContact,
    canDeleteContact,
    canExportContacts,
    canImportContacts,
    // Companies
    canViewCompanies,
    canCreateCompany,
    canEditCompany,
    canDeleteCompany,
    // Settings
    canManageAgents,
    canManageRoles,
    canManageInboxes,
    canManageAccount,
    canManageIntegrations,
    // Social
    canViewSocial,
    canManageSocial,
    canConfigureSocial,
  };
}
